import { getEntityTagFromSnake, getTickersFromSnakes } from '@toggle/helpers';
import { DetailedCatalystInsight } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { InsightTypePill } from '~/components/insight-type-pill/InsightTypePill';
import { usePriceMarketReactionData } from '~/hooks/use-price-market-reaction-data/usePriceMarketReactionData';
import { MarketReactionChart } from '~/modules/market-reaction-charts/MarketReactionChart';
import { useEntity } from '~/react-query-hooks/entities/useEntities';
import { useTheme } from '~/react-query-hooks/themes/useThemeOverview';
import { ThemeLogoIcon } from '~/views/theme-overview/components/theme-logo-icon/ThemeLogoIcon';
import { EntityHeaderWatchlist } from '~/widgets/earnings-insight-drawer/earnings-insight-widget/components/entity-header-watchlist/EntityHeaderWatchlist';

import { MarketCatalystReaction } from '../market-catalyst-reaction/MarketCatalystReaction';
import * as S from './CatalystDrawerHeader.styles';

export interface CatalystDrawerHeaderProps {
  identifier: string;
  catalystInsight: DetailedCatalystInsight;
  isCompanyCatalyst?: boolean;
}

export const CatalystDrawerHeader = ({
  identifier,
  catalystInsight,
  isCompanyCatalyst,
}: CatalystDrawerHeaderProps) => {
  const { t } = useTranslation('insights');
  const companyEntityTag = isCompanyCatalyst ? identifier : undefined;
  const themeId = !isCompanyCatalyst ? identifier : undefined;
  const entityTag = companyEntityTag
    ? companyEntityTag
    : getEntityTagFromSnake(catalystInsight.snakes[0]);
  const { data: entityData } = useEntity(entityTag);

  const { data: themeData } = useTheme({
    themeId,
  });

  const entity = entityData?.data;
  const isMultipleEntitiesChart = catalystInsight.snakes.length > 1;

  const {
    priceData,
    fromDateEST,
    toDateEST,
    lastProportionChange,
    lastKnownPrice,
    prevCloseData,
    lastKnownDate,
    isLoading: isPriceMarketReactionDataLoading,
  } = usePriceMarketReactionData({
    entityTag,
    ticker: entity?.subscribable_ticker ?? '',
    reportingDate: catalystInsight.created_at,
    enabled: isCompanyCatalyst,
    isMultipleEntitiesChart,
  });

  /* i18next-extract-mark-context-next-line ["financial_operational_updates", "corporate_actions_strategic_announcements", "management_leadership_changes", "legal_regulatory_events"] */
  const translatedSubCategory = t(
    `catalyst.subcategories.${catalystInsight.sub_category}`
  );

  return (
    <S.Root>
      <InsightTypePill
        createdAt={catalystInsight.created_at}
        subCategory={catalystInsight.sub_category}
        category={catalystInsight.category}
      />
      <div>
        <S.SubCategoryActionTypeWrapper>
          {themeId ? (
            <S.TitleWrapper>
              <span>{`${catalystInsight.action_type} · `}</span>
              {themeData && (
                <S.ThemeTitleWrapper>
                  <ThemeLogoIcon
                    logo_url={themeData.logo_url}
                    name={themeData.name}
                    size={20}
                  />
                  <S.ThemeTitle>{themeData.name}</S.ThemeTitle>
                </S.ThemeTitleWrapper>
              )}
            </S.TitleWrapper>
          ) : (
            `${translatedSubCategory} · ${catalystInsight.action_type}`
          )}
        </S.SubCategoryActionTypeWrapper>
        <S.Title>{catalystInsight.title}</S.Title>
      </div>
      <S.TitleGrid>
        {!isMultipleEntitiesChart && entity && (
          <S.WatchlistRow>
            <EntityHeaderWatchlist entity={entity} />
          </S.WatchlistRow>
        )}
        <S.ChartRow>
          <ErrorBoundaryWrapper>
            {isPriceMarketReactionDataLoading ? (
              <S.StyledSkeletonLoader />
            ) : isCompanyCatalyst ? (
              <MarketReactionChart
                fromDateEST={fromDateEST}
                toDateEST={toDateEST}
                lines={
                  priceData
                    ? [
                        {
                          data: priceData,
                          prevCloseData,
                          lastProportionChange,
                          lastKnownPrice,
                          lastKnownDate,
                        },
                      ]
                    : []
                }
                entities={entity ? [entity] : undefined}
              />
            ) : (
              <MarketCatalystReaction
                createdAt={catalystInsight.created_at}
                tags={catalystInsight.snakes.map(getEntityTagFromSnake)}
                tickers={getTickersFromSnakes(catalystInsight.snakes)}
              />
            )}
          </ErrorBoundaryWrapper>
        </S.ChartRow>
      </S.TitleGrid>
    </S.Root>
  );
};
